'use client'

import { usePathname } from 'next/navigation'
import { useEffect, useState } from 'react'

import { LOCALE, locales, type Locale } from '@/legacy-src/constants/locale.constants'

export type LocalizedLanguageTargets = Partial<Record<Locale, string>>

const isLocale = (value: string): value is Locale => locales.includes(value as Locale)

const readLocalizedTargets = (): LocalizedLanguageTargets => {
  const targets = Array.from(
    document.head.querySelectorAll<HTMLLinkElement>('link[rel="alternate"][hreflang][href]'),
  ).reduce<LocalizedLanguageTargets>((acc, link) => {
    const locale = link.hreflang.toLowerCase()

    if (!isLocale(locale)) {
      return acc
    }

    acc[locale] = link.href
    return acc
  }, {})

  const canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"][href]')

  if (canonical !== null) {
    targets[LOCALE] = canonical.href
  }

  return targets
}

/**
 * Reads localized targets from hreflang tags so shared client-side language
 * pickers stay aligned with SEO metadata and automatically support new public
 * pages that already emit alternates.
 */
export const useLocalizedLanguageTargets = () => {
  const pathname = usePathname()
  const [targets, setTargets] = useState<LocalizedLanguageTargets>({})

  useEffect(() => {
    const syncTargets = () => {
      setTargets(readLocalizedTargets())
    }

    syncTargets()

    const observer = new MutationObserver(syncTargets)
    observer.observe(document.head, { childList: true })

    return () => {
      observer.disconnect()
    }
  }, [pathname])

  return targets
}
